import { useEffect } from 'react'

import { isEmpty } from 'lodash'
import { useDispatch } from 'react-redux'

import { setCurrentUser } from 'store/currentUser/actions'
import { TYPE_BRAND } from 'store/currentUser/selectors'

import { CurrentUserInfo } from '../userInfo'

/**
 * Helper for translating text to selected language.
 *
 * We do not need to specify target language because
 * LocalizeJS know how to detect correct language from cookie.
 * Check if Localize object is defined on window object is
 * because without it tests will fail, or we will have to mock
 * it on many places. Also in cases when page fails to load
 * LocalizeJS due to unavailability it should fail gracefully
 * instead of throwing errors in console.
 */
export const translateText = (originalText: string) => {
  if (window.Localize) {
    return window?.Localize?.translate?.(originalText)
  }
  return originalText
}

export const defaultLanguages = [{ code: 'en', name: 'English' }]

export const getAvailableLanguages = () => {
  let availableLanguages = defaultLanguages
  if (window.Localize) {
    window?.Localize?.getAvailableLanguages?.((err, languages) => {
      if (!err) {
        availableLanguages = languages
      }
    })
  }
  availableLanguages.sort((a, b) => a.name.localeCompare(b.name))
  return availableLanguages
}

export const LANGUAGE_COOKIE = 'ljs-lang'
export const switchLanguage = (languageCode: string) => {
  if (window.Localize && languageCode) {
    window?.Localize?.setLanguage?.(languageCode)
  }
  if (languageCode) {
    document.cookie = `${LANGUAGE_COOKIE}=${languageCode};expires=90;path=/;`
  }
}

export const translateIfFeatureFlag = (flag: boolean) =>
  flag ? '' : 'notranslate'

export const useLocalize = (userInfo?: CurrentUserInfo) => {
  useEffect(() => {
    if (isEmpty(userInfo) || !window.Localize) return
    const isBrand = userInfo?.account?.type === TYPE_BRAND
    window?.Localize?.initialize?.({
      key: isBrand
        ? process.env.REACT_APP_BRAND_LOCALIZE_JS_PROJECT_KEY
        : process.env.REACT_APP_RETAIL_LOCALIZE_JS_PROJECT_KEY,
      rememberLanguage: true,
      saveNewPhrasesFromSource: true,
      translateBody: true,
      translateTitle: true,
      defaultLanguage: userInfo?.displayLanguage || 'en',
      targetLanguage: userInfo?.displayLanguage || 'en',
    })
  }, [userInfo])
}

export type Language = {
  code: string
  name: string
}

/**
 * This method is used to get the current language, the available languages and an update
 * method to change the language in our local storage, redux and zendesk.
 *
 * This is used for non-logged in users. The logged in users
 * must call the mutation setDisplayLanguageForUser alongside with this updateLanguage func.
 * For separation of concerns, the mutation hook lives in the
 * src/features/Navbar/NavbarRevamp/components/LocalizeDropdown/localizeDropdown.hooks.ts
 */
export const useStorageLanguages = () => {
  const dispatch = useDispatch()

  const storedLanguage = window.localStorage.getItem(LANGUAGE_COOKIE)
  const defaultLanguage = defaultLanguages[0]

  const code =
    !storedLanguage || storedLanguage === 'source'
      ? defaultLanguage.code
      : storedLanguage

  const languages: Language[] = getAvailableLanguages()

  const language: Language =
    languages.find((lang) => lang.code === code) || defaultLanguage

  const switchStorageLanguage = (code: string) => {
    switchLanguage(code)
    dispatch(
      setCurrentUser({
        displayLanguage: code,
      }),
    )
  }

  return { language, languages, switchStorageLanguage }
}
